import React, { useState } from 'react';
import styled from 'styled-components';
import AddContactSidebar from '~/scenes/Contacts/components/AddContactSidebar';
import formatToastMessage from '~/util/formatToastMessage';
import useAddToast from '~/hooks/useAddToast';
import { CONTACT_SIDEBAR_STATE } from '~/scenes/Contacts/components/ContactList';
import {
  ContactFilters__Input,
  ImportContactsFileFormat,
  ImportContactsV2MutationVariables,
  useImportContactsV2Mutation,
} from '~/graphql/types';
import ActionsContainer from './components/ActionsContainer';
import Button from '~/components/Button';
import Dropdown, { OptionOf } from '~/components/Dropdown';
import TEST_ID from './index.testid';
import StartFlowDropdown from './components/StartFlowDropdown';
import ImportContactsModal from './components/ImportContactsModal';
import OverviewListHeader from '~/components/OverviewListHeader';
import { FileType } from '~/util/constants';

export type WorkflowStepType =
  | 'MULTIPLE_CONTACTS'
  | 'MULTIPLE_CONTACTS_SUCCESS';

export type Props = {
  dataTestId?: string;
  /** Needed for importing contacts */
  contactsMeta: { accountId: string; officeId: string; userId: string };
  /** Function to refetch getContacts query and set version of Contacts table */
  onRefresh: () => void;
  /** Function to call when a delete action has finished */
  onDeleteContact: () => void;
  /** Function to call when add tags action has finished */
  onAddTags: () => void;
  /** Function to call when a change is made in the search field */
  onFilterChange: (text: string) => void;
  /** query and nodes fields returned from getContactsV2 query */
  queryVariables: {
    query?: string;
    filters: ContactFilters__Input;
  };
  /** Array of contact id's that are selected  */
  selectedContacts: Array<string>;
  allSelected: boolean;
  selectedContactAmount: number;

  sidebarProps: {
    /** Toggles the sidebar for the given contactSidebarState: single or multiple */
    toggleSidebar: (contactSidebarState: CONTACT_SIDEBAR_STATE) => void;
    /** Closes side bar */
    closeSidebar: () => void;
    /** Which sidebar to display: single or multiple */
    contactSidebarState: CONTACT_SIDEBAR_STATE | null;
    /** Sidebar's state: open or closed */
    showSidebar: boolean;
  };
};

// You can find mime here - https://learn.microsoft.com/en-us/previous-versions/office/office-2007-resource-kit/ee309278%28v=office.12%29
const XLSX =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLS = 'application/vnd.ms-excel';

export const text = {
  searchInputPlaceholder: 'Naam, adres, plaats...',
  title: 'Contacten',
  addContact: 'Nieuw contact',
  importContacts: 'Importeer contacten',
  showThisContact: 'Toon dit contact',
  contactCreatedToastTemplate: (name: string): string =>
    `Nieuw contact "${name}" toegevoegd!`,
  startFlow: 'Start flow',
  loadingFlows: 'Flows laden...',
};

const importContactsOptions: Array<OptionOf<ImportContactsFileFormat>> = [
  {
    label: 'Realworks - xls',
    payload: ImportContactsFileFormat.Realworks,
    key: ImportContactsFileFormat.Realworks,
  },
  {
    label: 'DatHuis - csv',
    payload: ImportContactsFileFormat.Csv,
    key: ImportContactsFileFormat.Csv,
  },
  {
    label: 'Accelerate - xlsx',
    payload: ImportContactsFileFormat.Accelerate,
    key: ImportContactsFileFormat.Accelerate,
  },
];

const allowedFileExtenstions: {
  [key in ImportContactsFileFormat]: Array<FileType>;
} = {
  [ImportContactsFileFormat.Accelerate]: [XLSX],
  [ImportContactsFileFormat.Csv]: ['text/csv'],
  [ImportContactsFileFormat.Realworks]: [XLSX, XLS],
};

const ContactsToolBar: React.FC<Props> = ({
  dataTestId,
  contactsMeta,
  onRefresh,
  onFilterChange,
  onAddTags,
  onDeleteContact,
  sidebarProps,
  ...rest
}) => {
  const addToast = useAddToast();

  const { toggleSidebar, closeSidebar, contactSidebarState, showSidebar } =
    sidebarProps;
  const [showImportContactsModal, setShowImportContactsModal] =
    useState<boolean>(false);

  const [importType, setImportType] = useState<ImportContactsFileFormat | null>(
    null,
  );

  const [workflowStep, setWorkflowStep] =
    useState<WorkflowStepType>('MULTIPLE_CONTACTS');

  const onAddMore = () => setWorkflowStep('MULTIPLE_CONTACTS');

  const onSuccessfulImport = () => {
    setWorkflowStep('MULTIPLE_CONTACTS_SUCCESS');
    setTimeout(() => onRefresh(), 2000);
  };

  const [importContacts, { loading }] = useImportContactsV2Mutation();

  const onImport = async (variables: ImportContactsV2MutationVariables) => {
    const result = await importContacts({
      variables,
    });

    if (!result || !result.data || !result.data.importContactsV2)
      return {
        errors: null,
        success: false,
      };

    if (
      result?.data?.importContactsV2.errors &&
      result?.data?.importContactsV2.errors.length > 0
    )
      return {
        errors: result?.data?.importContactsV2.errors,
        success: false,
      };

    onSuccessfulImport();

    return {
      errors: null,
      success: true,
    };
  };

  return (
    <>
      <Container data-testid={dataTestId} {...rest}>
        <AddContactSidebar
          contactsMeta={contactsMeta}
          onNewContactAdded={({ insertContact }) => {
            closeSidebar();
            addToast([
              formatToastMessage(
                text.contactCreatedToastTemplate(insertContact.name),
                'success',
                {
                  to: `/-/contacts/${insertContact.id}`,
                  label: text.showThisContact,
                },
              ),
            ]);

            // Refetching contacts delayed because it may take a while until backend returns the newly added contact
            setTimeout(() => onRefresh(), 3000);
          }}
          isShowing={
            showSidebar &&
            contactSidebarState === CONTACT_SIDEBAR_STATE.SINGLE_CONTACT
          }
          hide={() => {
            closeSidebar();
          }}
        />

        <OverviewListHeader
          dataTestId="contact-list-header"
          title={text.title}
          buttons={[
            {
              key: 'overview-header-add-contact-button',
              node: (
                <Button
                  size="medium"
                  icon="plus"
                  onClick={() =>
                    toggleSidebar(CONTACT_SIDEBAR_STATE.SINGLE_CONTACT)
                  }
                  appearance="secondary"
                  data-testid={TEST_ID.ADD_SINGLE_CONTACT_BUTTON}
                  label={text.addContact}
                />
              ),
            },
            {
              key: 'overview-header-start-flow',
              node: <StartFlowDropdown {...rest} />,
            },
            {
              key: 'overview-header-import-contacts-button',
              node: (
                <Dropdown
                  options={importContactsOptions}
                  onChange={({ option: { payload } }) => {
                    setShowImportContactsModal(true);
                    toggleSidebar(CONTACT_SIDEBAR_STATE.MULTIPLE_CONTACTS);
                    setImportType(payload);
                  }}
                  dataTestId={TEST_ID.IMPORT_MULTIPLE_CONTACTS_DROPDOWN}
                  placeholder={text.importContacts}
                />
              ),
            },
            {
              key: 'overview-header-actions-button',
              node: (
                <ActionsContainer
                  {...rest}
                  onAddTags={onAddTags}
                  onDeleteContact={onDeleteContact}
                />
              ),
            },
          ]}
          searchInputPlaceholder={text.searchInputPlaceholder}
          onFilterChange={onFilterChange}
        />
      </Container>

      {showImportContactsModal && importType && (
        <ImportContactsModal
          workflowStep={workflowStep}
          onAddMore={onAddMore}
          allowedFileExtensions={allowedFileExtenstions[importType] ?? []}
          loading={loading}
          onImport={onImport}
          fileFormat={importType}
          onClose={() => {
            setShowImportContactsModal(false);
            setWorkflowStep('MULTIPLE_CONTACTS');
          }}
        />
      )}
    </>
  );
};

const Container = styled.div<{}>``;

export default ContactsToolBar;
